'use client';

/**
 * Auth Context Provider
 * Manages authentication state and provides auth methods to components
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import {
  login as jwtLogin,
  register as jwtRegister,
  logout as jwtLogout,
  getCurrentUser,
  isAuthenticated,
  loginWithGoogle,
  loginWithMicrosoft,
  handleOAuthCallback,
  isInviteSession,
  resolveResponseIdFromUrl,
  getInviteIdentity,
  AuthUser,
  LoginCredentials,
  PendingVerification,
  RegisterData,
} from './jwt-client';

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  /** True when the current principal is an account-less invite session (ADR-0003). */
  isInviteSession: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<PendingVerification>;
  logout: () => Promise<void>;
  loginWithGoogle: () => void;
  loginWithMicrosoft: () => void;
  refreshUser: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  // Gate any localStorage-derived value behind mount: it must not influence the first (hydration)
  // render, or the server HTML (no localStorage) and client HTML diverge. Set true post-hydration.
  const [mounted, setMounted] = useState<boolean>(false);
  const pathname = usePathname();

  useEffect(() => { setMounted(true); }, []);

  // Initialize auth state on mount
  useEffect(() => {
    async function loadUser() {
      // Check for OAuth callback — called for its side effect (storing tokens).
      handleOAuthCallback();

      // Load user from API
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      setLoading(false);
    }

    loadUser();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    await jwtLogin(credentials);
    const currentUser = await getCurrentUser();
    setUser(currentUser);
  };

  const register = async (data: RegisterData) => jwtRegister(data);

  const logout = async () => {
    await jwtLogout();
    setUser(null);
  };

  const refreshUser = async () => {
    const currentUser = await getCurrentUser();
    setUser(currentUser);
  };

  // Per-record identity (#137): when the current route targets a Work Response we hold an invite
  // session for, that record's invitee — decoded from its own token — is the identity shown, not
  // the most-recently-opened global session. This keeps two concurrent invite tabs (e.g. a vendor
  // response and a client brief) each showing their own person. Real users (no per-record invite
  // token for the route) fall through to the global user unchanged.
  // Only after mount (post-hydration) — getInviteIdentity reads localStorage, which is empty on the
  // server; applying it during the first render would mismatch the SSR HTML.
  const recordResponseId = mounted && pathname ? resolveResponseIdFromUrl(pathname) : null;
  const recordIdentity = recordResponseId ? getInviteIdentity(recordResponseId) : null;
  const effectiveUser = recordIdentity ?? user;

  const value: AuthContextType = {
    user: effectiveUser,
    loading,
    isAuthenticated: isAuthenticated(),
    isInviteSession: isInviteSession(effectiveUser),
    login,
    register,
    logout,
    loginWithGoogle,
    loginWithMicrosoft,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
